import React from "react";
import useHighlightStore, {
  HighlightItem,
  ItemType,
} from "../zstore/highlightStore";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import useInvestorEvidenceRequestStore from "../zstore/investorEvidenceRequest";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { scrollTo } from "../utils/dom";
import useMessage, { AntDMessage } from "./useMessage";
import { first } from "lodash";
import { InvestorMarker } from "../api/transformers/marker";
import {
  EvidenceRequestStatusEnum,
  NeglectableEvidenceRequestStatus,
} from "../constant/requiredEvidence";
import { InvestorEvidenceRequestOverview } from "../api/transformers/requiredEvidence";
import { AmlStatus, OnboardingModel } from "../constant/amlConstant";
import useAssociatedPartyStore from "../zstore/associatedPartyStore";
import { useShallow } from "zustand/react/shallow";
import { useSelector } from "react-redux";
import { selectFromAmlStore } from "../amlSlice";
import { getEntityTypeCheckers } from "../utils/investor";
import { NoneGroupMarkers } from "../constant/markerConstant";

const dataSelector = <T extends { data: unknown }>(state: T): T["data"] =>
  state.data;

type HandlerTool = {
  message: AntDMessage;
  addHighlightItems: (items: HighlightItem[]) => void;
};

type ValidateOption = {
  scrollToFirstError: boolean;
  showMessage: boolean;
};

const useStatusEditValidation = () => {
  const batchAddHighlightItems = useHighlightStore(
    (state) => state.batchAddItem
  );
  const investorMarkers = useInvestorMarkerStore(dataSelector);
  const evidenceRequests = useInvestorEvidenceRequestStore(dataSelector);
  const amlDetail = useInvestorAmlDetailStore(dataSelector);
  const { associatedParties, associatedPartyLoading } = useAssociatedPartyStore(
    useShallow((state) => ({
      associatedParties: state.data,
      associatedPartyLoading: state.loading,
    }))
  );
  const entityTypes = useSelector(selectFromAmlStore((state) => state.entityTypes));
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const message = useMessage();
  const validate = ({ scrollToFirstError = true, showMessage = true } = {}) => {
    const tools: HandlerTool = {
      message,
      addHighlightItems: batchAddHighlightItems,
    };
    const options: ValidateOption = {
      scrollToFirstError,
      showMessage,
    };
    // investorMarkers not ready
    if (!investorMarkers) {
      message.info("Loading markers of investor, please wait...");
      return false;
    }
    // handle referred markers.
    const referredMarkers = investorMarkers.filter(
      (el) => !el.removed && el.isReferred
    );
    if (referredMarkers.length > 0) {
      handleReferredMarkerError(referredMarkers, tools, options);
      return false;
    }
    // handle restrictive markers.
    const restrictiveMarkers = investorMarkers.filter(
      (el) => !el.removed && el.isRestrictive
    );
    if (restrictiveMarkers.length > 0) {
      handleRestrictiveMarkerError(restrictiveMarkers, tools, options);
      return false;
    }
    // handle evidence request.
    const pendingEvidenceRequest = evidenceRequests.filter(
      (el) =>
        !NeglectableEvidenceRequestStatus.includes(
          el.status as EvidenceRequestStatusEnum
        )
    );
    if (pendingEvidenceRequest.length > 0) {
      handlePendingEvidenceRequest(pendingEvidenceRequest, tools, options);
      return false;
    }
    // amlDetails not ready
    if (!amlDetail) {
      message.info("Loading investor onboarding model, please wait...");
      return false;
    }
    // Corporate/Trust parties must be compliant before verification
    const isCorporateOrTrust =
      entityTypeCheckers.isCorporate(amlDetail.entityType) ||
      entityTypeCheckers.isTrust(amlDetail.entityType);
    if (isCorporateOrTrust) {
      if (associatedPartyLoading) {
        if (showMessage) {
          message.info({
            content: "Loading associated parties, please wait...",
            style: { marginTop: "45vh" },
          });
        }
        return false;
      }
      const isPartyCompliant = associatedParties.every(
        (p) => p.amlStatus === AmlStatus.ACT || p.amlStatus === AmlStatus.NRQ
      );
      if (!isPartyCompliant) {
        if (showMessage) {
          message.info({
            content:
              "All Associated Parties must be Verified or Not Required before the entity can be verified.",
            style: { marginTop: "45vh" },
          });
        }
        scrollToFirstError && scrollTo("associated-parties-section", { block: "center" });
        return false;
      }
    }
    const investorUnderRelianceModel =
      amlDetail.onboardingModel === OnboardingModel.RELIANCE;
    const kycMarkerActive = investorMarkers.some(
      (el) => !el.removed && el.code === NoneGroupMarkers.KYC
    );
    if (investorUnderRelianceModel && kycMarkerActive) {
      message.info({
        content:
          "The Uplift Process cannot complete if the KYC information is outstanding.",
        style: {
          marginTop: "45vh",
        },
      });
      return false;
    }
    return true;
  };

  return { validate };
};

function handleReferredMarkerError(
  referredMarkers: InvestorMarker[],
  tools: HandlerTool,
  options: ValidateOption
) {
  const referredHighlightedItems = referredMarkers.map((el) => ({
    id: el.code,
    type: ItemType.MARKER,
    message:
      "This marker must be updated before the outcome can be recorded as verified.",
  }));
  if (options.showMessage) {
    tools.message.info({
      content:
        "The highlighted fields must be updated before the outcome can be recorded as verified.",
      style: {
        marginTop: "45vh",
      },
    });
  }
  tools.addHighlightItems(referredHighlightedItems);
  options.scrollToFirstError &&
    scrollTo(first(referredHighlightedItems)!.id, { block: "center" });
}

function handleRestrictiveMarkerError(
  restrictiveMarkers: InvestorMarker[],
  tools: HandlerTool,
  options: ValidateOption
) {
  const restrictiveHighlightedItems = restrictiveMarkers.map((el) => ({
    id: el.code,
    type: ItemType.MARKER,
    message:
      "This restrictive marker must be updated before the outcome can be recorded as verified.",
  }));
  if (options.showMessage) {
    tools.message.info({
      content:
        "Any restrictive markers must be updated before the outcome can be recorded as verified.",
      style: {
        marginTop: "45vh",
      },
    });
  }
  tools.addHighlightItems(restrictiveHighlightedItems);
  options.scrollToFirstError &&
    scrollTo(first(restrictiveHighlightedItems)!.id, { block: "center" });
}

function handlePendingEvidenceRequest(
  pendingRequest: InvestorEvidenceRequestOverview[],
  tools: HandlerTool,
  options: ValidateOption
) {
  if (options.showMessage) {
    tools.message.info({
      content: "There are pending evidence requests that need to be addressed.",
      style: {
        marginTop: "45vh",
      },
    });
  }
  tools.addHighlightItems(
    pendingRequest.map((el) => ({
      id: el.code,
      type: ItemType.EVIDENCE_REQUEST,
    }))
  );
  options.scrollToFirstError &&
    scrollTo(first(pendingRequest)!.code, { block: "center" });
}

export default useStatusEditValidation;
